import "./App.css";
import { Route, Routes, BrowserRouter as Router } from "react-router-dom";
import Login from "./pages/auth/Login";
import Dashboard from "./pages/Dashboard";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { SocketProvider } from "./context/SocketContext";
import { OnboardingProvider } from "./context/OnboardingContext";
import { WorkspaceProvider } from "./providers/WorkspaceProvider";

import ProtectedRoutes from "./Routes/ProtectedRoutes";
import EmployeeDetails from "./pages/employee_details";
import UploadEmployees from "./pages/employee_details/UploadEmployees";
import AddEmployeeDetails from "./pages/employee_details/AddEmployeeDetails";

import LeaveApplication from "./pages/leave_application";

import LeaveApplied from "./pages/leaves_applied";
import EditEmployee from "./pages/employee_details/EditEmployee";
import Department from "./pages/department";
import LeaveDetail from "./pages/employee_details/LeaveDetail";
import Salary from "./pages/Salary";
import Holidays from "./pages/holidays";
import Attendance from "./pages/attendance";
import UploadAttendance from "./pages/attendance/UploadAttendance";
import ViewAttendance from "./pages/attendance/ViewAttendance";
import PageNotFound from "./pages/errors/PageNotFound";
import EditLeaves from "./pages/employee_details/EditLeaves";
import ResignedEmployeeDetails from "./pages/employee_details/ResignedEmployeeDetails";
import SalaryLogs from "./pages/Salary/SalaryLogs";
import Shifts from "./pages/shifts";
import Employment from "./pages/employment";
import Esic from "./pages/Salary/esic";
import ProvidentFund from "./pages/Salary/pf";
import ShiftMapping from "./pages/shifts/ShiftMapping";
import ShiftRotation from "./pages/shifts/ShiftRotation";
import ShiftsSettings from "./pages/shifts/settings";
import SingleEmployeeDetail from "./pages/employee_details/SingleEmployeeDetail";
import SingleEmployeeSalary from "./pages/Salary/EmployeeSalaryDetails/SingleEmployeeSalary";
import EmployeeSalaryDetails from "./pages/Salary/EmployeeSalaryDetails";
import BaseSalary from "./pages/Salary/BaseSalary";
import ProfessionalTax from "./pages/Salary/PT";
import EmployeePromotion from "./pages/employee_details/EmployeePromotion";
import LeaveEditHistory from "./pages/leaves_applied/LeaveEditHistory";
import ResignEmployee from "./pages/employee_details/ResignEmployee";
import WebCheckIns from "./pages/webCheckIns";
import HandBook from "./pages/HandBooks";
import ShiftDetails from "./pages/shifts/ShiftDetails";
import SinglePersonWebcheckin from "./pages/webCheckIns/SinglePersonWebcheckin";
import Layout from "./components/layout/Layout";
import { GOOGLE_CLIENT_ID, ROLES } from "./config";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { PunchIn, PunchLogs } from "./pages/punchin";
import MarkWFHAttendance from "./pages/WFH/MarkWFHAttendance";
import MarkWFHAttendanceForm from "./pages/WFH/MarkWFHAttendanceForm";
import WFHApplicationForm from "./pages/WFH/WFHApplicationForm";
import { Toaster } from "react-hot-toast";
import WFHApproval from "./pages/WFH/WFHApproval";
import ListWFHEmployee from "./pages/WFH/ListWFHEmployee";
import BonusListing from "./pages/bonus/BonusListing";
import AdvanceAndBonus from "./pages/Salary/AdvanceAndBonus";
import AttendanceReport from "./pages/attendance/AttendanceReport";
import ForgotPassword from "./pages/auth/ForgotPassword";
import SingleEmployeeDocuments from "./pages/employee_details/SingleEmployeeDocuments";
import Invite from "./pages/auth/Invite";
import CompanySettings from "./pages/company_settings/CompanySettings";
import LeaveStructure from "./pages/company_settings/LeaveStructure";
import SalaryStructure from "./pages/company_settings/SalaryStructure";
import Users from "./pages/LiveTracking/users";
import Trips from "./pages/LiveTracking/trips";
import MyAccount from "./pages/my_account";
import EditAccount from "./pages/my_account/EditAccount";
import Chats from "./pages/chats";
import UserAttendance from "./pages/user_attendance";
import UserLeaves from "./pages/leaves_applied/UserLeaves";
import Geofencing from "./pages/geofencing";
import AddSalaryComponent from "./pages/company_settings/AddSalaryComponent";
import EarningsSalaryComponents from "./pages/company_settings/salary_structure/EarningsSalaryComponents";
import EditSalaryComponent from "./pages/company_settings/salary_structure/EditSalaryComponent";
import StatutorySalaryComponents from "./pages/company_settings/salary_structure/StatutorySalaryComponents";
import SalaryTemplatesList from "./pages/salary_templates/SalaryTemplatesList";
import ManageSalaryTemplate from "./pages/salary_templates/ManageSalaryTemplate";
import AddUserSalaryDetails from "./pages/Salary/EmployeeSalaryDetails/AddUserSalaryDetails";
import EditUserSalaryDetails from "./pages/Salary/EmployeeSalaryDetails/EditUserSalaryDetails";
import ApprovalsPage from "./pages/approvals/ApprovalsPage";
import EmployeeTdsLedger from "./pages/employee_details/EmployeeTdsLedger";
import InvestmentsAndProofs from "./pages/employee_details/InvestmentsAndProofs/InvestmentsAndProofs";
import InvestmentDeclarationCTR from "./pages/employee_details/InvestmentsAndProofs/InvestmentDeclaration/InvestmentDeclarationCTR";
import ProofOfInvestmentsCTR from "./pages/employee_details/InvestmentsAndProofs/ProofOfInvestments/ProofOfInvestmentsCTR";
import EditInvestmentDeclaration from "./pages/employee_details/InvestmentsAndProofs/InvestmentDeclaration/EditInvestmentDeclaration";
import EditProofOfInvestments from "./pages/employee_details/InvestmentsAndProofs/ProofOfInvestments/EditProofOfInvestments";
import RegularizationRequests from "./pages/attendance/RegularizationRequests";
import Onboarding from "./pages/onboarding/Onboarding";
import BrandMetadataSync from "./components/BrandMetadataSync";

function App() {
  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <Router>
        <BrandMetadataSync />
        <Toaster position="top-right" />
        <SocketProvider>
          <WorkspaceProvider>
            <OnboardingProvider>
              <Routes>
                <Route element={<Layout />}>
                  {/* Shared Authenticated Routes (Company Admin, Employee) */}
                  <Route
                    element={
                      <ProtectedRoutes
                        allowedRoles={[ROLES.COMPANY_ADMIN, ROLES.EMPLOYEE]}
                      />
                    }
                  >
                    <Route path="/" exact element={<Dashboard />} />
                    <Route path="/onboarding" exact element={<Onboarding />} />
                    <Route
                      path="/public-holidays"
                      exact
                      element={<Holidays />}
                    />
                    <Route path="/hand-book" exact element={<HandBook />} />
                    <Route path="/chats" exact element={<Chats />} />
                    <Route
                      path="/leaves/leave-edit-history/:id"
                      exact
                      element={<LeaveEditHistory />}
                    />
                    <Route
                      path="/edit-leaves/:leaveId"
                      exact
                      element={<EditLeaves />}
                    />
                  </Route>

                  {/* Company Admin Only Routes */}
                  <Route
                    element={
                      <ProtectedRoutes allowedRoles={[ROLES.COMPANY_ADMIN]} />
                    }
                  >
                    <Route
                      path="/employee-details"
                      exact
                      element={<EmployeeDetails />}
                    />
                    <Route
                      path="/leaves/:company_employee_id/:username"
                      exact
                      element={<LeaveDetail />}
                    />
                    <Route
                      path="/employee-details/upload"
                      exact
                      element={<UploadEmployees />}
                    />
                    <Route path="/bonus" exact element={<BonusListing />} />
                    <Route
                      path="/add-employee-details"
                      exact
                      element={<AddEmployeeDetails />}
                    />
                    <Route
                      path="/edit-employee-details/:company_employee_id"
                      exact
                      element={<EditEmployee />}
                    />
                    <Route path="/salary" exact element={<BaseSalary />} />
                    <Route
                      path="/advance-and-bonus"
                      exact
                      element={<AdvanceAndBonus />}
                    />
                    <Route
                      path="/attendance-report"
                      exact
                      element={<AttendanceReport />}
                    />
                    <Route path="/shifts" exact element={<Shifts />} />
                    <Route
                      path="/employment-management"
                      exact
                      element={<Employment />}
                    />
                    <Route path="/attendance" exact element={<Attendance />} />
                    <Route
                      path="/attendance/upload"
                      exact
                      element={<UploadAttendance />}
                    />
                    <Route
                      path="/regularization-requests"
                      exact
                      element={<RegularizationRequests />}
                    />
                    <Route
                      path="/web-check-ins"
                      exact
                      element={<WebCheckIns />}
                    />
                    <Route
                      path="/leave-applied"
                      exact
                      element={<LeaveApplied />}
                    />
                    <Route
                      path="/provident-fund"
                      exact
                      element={<ProvidentFund />}
                    />
                    <Route path="/esic" exact element={<Esic />} />
                    <Route
                      path="/professional-tax"
                      exact
                      element={<ProfessionalTax />}
                    />
                    <Route path="/salary/logs" exact element={<SalaryLogs />} />
                    <Route
                      path="/employee-salary/:id/:username"
                      exact
                      element={<EmployeeSalaryDetails />}
                    />
                    <Route
                      path="/employee-salary/:id/:username/add-salary"
                      exact
                      element={<AddUserSalaryDetails />}
                    />
                    <Route
                      path="/employee-salary/:id/:username/edit-salary/:salaryId"
                      exact
                      element={<EditUserSalaryDetails />}
                    />
                    <Route
                      path="/employee-salary-details/:id/:username"
                      exact
                      element={<SingleEmployeeSalary />}
                    />
                    <Route
                      path="/employee-details/:company_employee_id/:username"
                      exact
                      element={<SingleEmployeeDetail />}
                    />
                    <Route
                      path="/employee-details/:company_employee_id/investments-and-proofs"
                      exact
                      element={<InvestmentsAndProofs />}
                    />
                    <Route
                      path="/employee-details/:company_employee_id/investments-and-proofs/investment-declaration/:financial_year_id/compare-tax-regimes"
                      exact
                      element={<InvestmentDeclarationCTR />}
                    />
                    <Route
                      path="/employee-details/:company_employee_id/investments-and-proofs/proof-of-investments/compare-tax-regimes"
                      exact
                      element={<ProofOfInvestmentsCTR />}
                    />
                    <Route
                      path="/employee-details/:company_employee_id/investments-and-proofs/investment-declaration/:financial_year_id/edit"
                      exact
                      element={<EditInvestmentDeclaration />}
                    />
                    <Route
                      path="/employee-details/:company_employee_id/investments-and-proofs/proof-of-investments/:financial_year_id/edit"
                      exact
                      element={<EditProofOfInvestments />}
                    />
                    <Route
                      path="/employee-documents/:company_employee_id/:username"
                      exact
                      element={<SingleEmployeeDocuments />}
                    />
                    <Route
                      path="/employee-promotion/:id/:username"
                      exact
                      element={<EmployeePromotion />}
                    />
                    <Route
                      path="/employee-tds-ledger/:id/:username"
                      exact
                      element={<EmployeeTdsLedger />}
                    />
                    <Route
                      path="/view-attendance/:id"
                      exact
                      element={<ViewAttendance />}
                    />
                    <Route
                      path="/web-check-ins/:company_employee_id"
                      exact
                      element={<SinglePersonWebcheckin />}
                    />
                    <Route
                      path="/resigned-employee-details"
                      exact
                      element={<ResignedEmployeeDetails />}
                    />
                    <Route
                      path="/resign-employee/:id/:username"
                      exact
                      element={<ResignEmployee />}
                    />
                    <Route path="/department" exact element={<Department />} />
                    <Route
                      path="/work-from-home-approval"
                      exact
                      element={<WFHApproval />}
                    />

                    {/* Live Tracking */}
                    <Route path="/live_tracking">
                      <Route path="users" element={<Users />} />
                      <Route path="users/trips" element={<Trips />} />
                    </Route>

                    {/* Company Settings */}
                    <Route path="/company-settings">
                      <Route path="" element={<CompanySettings />} />
                      <Route
                        path="leave-structure"
                        element={<LeaveStructure />}
                      />
                      <Route
                        path="salary-structure"
                        element={<SalaryStructure />}
                      >
                        <Route path="" element={<EarningsSalaryComponents />} />
                        <Route
                          path="statutory-components"
                          element={<StatutorySalaryComponents />}
                        />
                      </Route>
                      <Route
                        path="salary-structure/add"
                        element={<AddSalaryComponent />}
                      />
                      <Route
                        path="salary-structure/edit/:id"
                        element={<EditSalaryComponent />}
                      />
                    </Route>

                    {/* Salary Templates */}
                    <Route
                      path="/salary-templates"
                      element={<SalaryTemplatesList />}
                    />
                    <Route
                      path="/salary-templates/manage-salary-template/:id"
                      element={<ManageSalaryTemplate />}
                    />

                    {/* Approvals */}
                    <Route
                      path="/approvals"
                      exact
                      element={<ApprovalsPage />}
                    />
                    <Route path="/geofencing" exact element={<Geofencing />} />
                  </Route>

                  {/* Employee Only Routes */}
                  <Route
                    element={
                      <ProtectedRoutes allowedRoles={[ROLES.EMPLOYEE]} />
                    }
                  >
                    <Route path="/my-account" exact element={<MyAccount />} />
                    <Route
                      path="/my-account/edit"
                      exact
                      element={<EditAccount />}
                    />
                    <Route
                      path="/user-attendance"
                      exact
                      element={<UserAttendance />}
                    />
                    <Route
                      path="/leave-application"
                      exact
                      element={<LeaveApplication />}
                    />
                    <Route
                      path="/leaves-applied"
                      exact
                      element={<UserLeaves />}
                    />
                    <Route
                      path="/shift-details"
                      exact
                      element={<ShiftDetails />}
                    />
                    <Route
                      path="/attendance-punch-in"
                      exact
                      element={<PunchIn />}
                    />
                    <Route path="/punchout" exact element={<PunchIn />} />
                    <Route
                      path="/attendance-punch-in/logs"
                      exact
                      element={<PunchLogs />}
                    />
                    <Route
                      path="/work-from-home"
                      exact
                      element={<MarkWFHAttendance />}
                    />
                    <Route
                      path="/wfh/mark"
                      exact
                      element={<MarkWFHAttendanceForm />}
                    />
                    <Route
                      path="/wfh/apply"
                      exact
                      element={<WFHApplicationForm />}
                    />
                    <Route path="/wfh" exact element={<ListWFHEmployee />} />
                  </Route>
                </Route>

                <Route path="*" exact element={<PageNotFound />} />
              </Routes>
            </OnboardingProvider>
          </WorkspaceProvider>
        </SocketProvider>
      </Router>
    </GoogleOAuthProvider>
  );
}

export default App;

{
  /* <Route path='/attendance' exact element={<ProtectedRoutes accessTo={['admin']}><Attendance /></ProtectedRoutes>} />

                <Route path='/employe-details' exact element={<ProtectedRoutes accessTo={['admin', 'hr']}><EmployeeDetails /></ProtectedRoutes>} />
                <Route path='/employe-details/:id/:username' exact element={<ProtectedRoutes accessTo={['admin', 'hr']}><SingleEmployeeDetail /></ProtectedRoutes>} />
                <Route path='/employee-salary-details/:id/:username' exact element={<ProtectedRoutes accessTo={['admin', 'hr']}><SingleEmployeeSalary /></ProtectedRoutes>} />
                <Route path='/employee-promotion/:id/:username' exact element={<ProtectedRoutes accessTo={['admin', 'hr']}><EmployeePromotion /></ProtectedRoutes>} />
                <Route path='/resign-employee/:id/:username/:type' exact element={<ProtectedRoutes accessTo={['admin', 'hr']}><ResignEmployee /></ProtectedRoutes>} />
                <Route path='/resigned-employee-details' exact element={<ProtectedRoutes accessTo={['admin', 'hr']}><ResignedEmployeeDetails /></ProtectedRoutes>} />
                <Route path='/salary/logs' exact element={<ProtectedRoutes accessTo={['admin', 'hr']}><SalaryLogs /></ProtectedRoutes>} />
                <Route path='/esic' exact element={<ProtectedRoutes accessTo={['admin', 'hr']}><Esic /></ProtectedRoutes>} />
                <Route path='/professional-tax' exact element={<ProtectedRoutes accessTo={['admin', 'hr']}><ProfessionalTax /></ProtectedRoutes>} />
                <Route path='/provident-fund' exact element={<ProtectedRoutes accessTo={['admin', 'hr']}><ProvidentFund /></ProtectedRoutes>} />
                <Route path='/employee-salary/:id/:username' exact element={<ProtectedRoutes accessTo={['admin', 'hr']}><EmployeeSalaryDetails /></ProtectedRoutes>} />
                <Route path='/appointment' exact element={<ProtectedRoutes accessTo={['admin', 'hr']}><Appointment /></ProtectedRoutes>} />
                <Route path='/leave-applied' exact element={<ProtectedRoutes accessTo={['admin', 'hr']}><LeaveApplied /></ProtectedRoutes>} />
                <Route path='/public-holidays' exact element={<ProtectedRoutes accessTo={['admin', 'hr', 'employee', 'consultant']}><Holidays /></ProtectedRoutes>} />
                <Route path='/add-employe-details' exact element={<ProtectedRoutes accessTo={['admin', 'hr']}><AddEmployeeDetails /></ProtectedRoutes>} />
                <Route path='/edit-employe-details/:userType/:id' exact element={<ProtectedRoutes accessTo={['admin', 'hr']}><EditEmployee /></ProtectedRoutes>} />
                <Route path='/leave-application' exact element={<ProtectedRoutes accessTo={['employee', 'consultant']}><LeaveApplication /></ProtectedRoutes>} />
                <Route path='/leaves' exact element={<ProtectedRoutes accessTo={['employee', 'consultant']}><LeaveDetail /></ProtectedRoutes>} />
                <Route path='/shifts' exact element={<ProtectedRoutes accessTo={['admin', 'hr']}><Shifts /></ProtectedRoutes>} />
                <Route path='/employee-shift-mapping' exact element={<ProtectedRoutes accessTo={['admin', 'hr']}><ShiftMapping /></ProtectedRoutes>} />
                <Route path='/employment-management' exact element={<ProtectedRoutes accessTo={['admin', 'hr']}><Employment /></ProtectedRoutes>} />
                <Route path='/shift-rotation' exact element={<ProtectedRoutes accessTo={['admin', 'hr']}><ShiftRotation /></ProtectedRoutes>} />
                <Route path='/shift-settings' exact element={<ProtectedRoutes accessTo={['admin', 'hr']}><ShiftsSettings /></ProtectedRoutes>} />
                <Route path='/shift-details' exact element={<ProtectedRoutes accessTo={['employee', 'consultant']}><ShiftDetails /></ProtectedRoutes>} />



                <Route path='/leaves/:id' exact element={<ProtectedRoutes accessTo={'all'}><LeaveDetail /></ProtectedRoutes>} />
                <Route path='/leaves/leave-edit-history/:id' exact element={<ProtectedRoutes accessTo={'all'}><LeaveEditHistory /></ProtectedRoutes>} />
                <Route path='/edit-leaves/:leaveId' exact element={<ProtectedRoutes accessTo={'all'}><EditLeaves /></ProtectedRoutes>} />
                <Route path='/add-leave-details' exact element={<ProtectedRoutes accessTo={['employee', 'consultant']}><LeaveApplicationForm /></ProtectedRoutes>} />
                <Route path='/appointment-letter/:id' exact element={<ProtectedRoutes accessTo={'all'}><AppointmentLetter /></ProtectedRoutes>} />
                <Route path='/open-leave' exact element={<ProtectedRoutes accessTo={'all'}><LeavesApplied /></ProtectedRoutes>} />
                <Route path='/department' exact element={<ProtectedRoutes accessTo={'all'}><Department /></ProtectedRoutes>} />
                <Route path='/view-attendance/:id' exact element={<ProtectedRoutes accessTo={'all'}><ViewAttendance /></ProtectedRoutes>} />
                <Route path='/department' exact element={<ProtectedRoutes accessTo={'all'}><Department /></ProtectedRoutes>} />
                <Route path='/web-check-ins' exact element={<ProtectedRoutes accessTo={'all'}><WebCheckIns /></ProtectedRoutes>} />
                <Route path='/web-check-ins/:id' exact element={<ProtectedRoutes accessTo={'all'}><SinglePersonWebcheckin /></ProtectedRoutes>} />
                <Route path='/hand-book' exact element={<ProtectedRoutes accessTo={'all'}><HandBook /></ProtectedRoutes>} /> */
}
